/**
 * Cross-cutting contract primitives used by `errors.ts`, `admins.ts` and `ops.ts`.
 *
 * Scope discipline (T005): this module holds only what those three modules actually need today.
 * Domain models (Account, Question, Enrollment, …) belong to `packages/domain` and to the
 * operation shapes frozen at T024 — nothing is declared here in anticipation of them.
 *
 * `BOUNDARIES.md` § 2: `zod` is the only runtime dependency this workspace may have.
 *
 * Every exported contract list in this workspace is `Object.freeze`d, not merely `as const`. `as const`
 * is erased at compile time: a caller in plain JavaScript, or one that casts, can `push` onto an
 * exported array and desynchronize the guards that read it from the schemas and registries built from
 * it. The freeze makes that attempt throw, and `Readonly<readonly [...]>` is the same readonly tuple,
 * so `z.enum` and the mapped types over these lists still compile unchanged.
 */
import { z } from 'zod';

/**
 * The deployed Cloud Functions v2 units: the seven of the accepted packaging table
 * (`ARCHITECTURE.md` § D-1, `contracts/operations.md` § Packaging) plus `uploadSweep`, the hourly
 * FR-013 upload sweep added by the 2026-09-25 release audit (R10). This list is the deployment
 * surface, not the dispatch surface: `fileGateway` is an `onRequest` streaming endpoint and
 * `uploadSweep` a scheduled job, and neither ever receives an `{ op, params }` envelope (see `ops.ts`).
 */
export const DEPLOYED_FUNCTIONS = Object.freeze([
  'bootstrapApi',
  'studentApi',
  'adminApi',
  'fileGateway',
  'aiWorker',
  'hourlyAiRun',
  'purgeDaily',
  'uploadSweep',
] as const);

export type DeployedFunctionName = (typeof DEPLOYED_FUNCTIONS)[number];

export const deployedFunctionNameSchema = z.enum(DEPLOYED_FUNCTIONS);

/**
 * A stable identifier for a user-facing Georgian message.
 *
 * The key — never a server-produced string — is what crosses the wire and what the client looks up
 * in its own Georgian catalogue (`apps/web/src/app/messages.ts`, owned by T007). `BOUNDARIES.md` § 6:
 * "the client never composes an error message from a server string".
 *
 * The template literal keeps the dotted namespace shape in the type system; `MESSAGE_KEY_PATTERN`
 * enforces the full lexical rule at runtime.
 */
export type MessageKey = `${string}.${string}`;

/** `namespace.name` (or deeper) in lowerCamelCase segments — ASCII only, so the key is never itself text. */
export const MESSAGE_KEY_PATTERN = /^[a-z][A-Za-z0-9]*(?:\.[a-z][A-Za-z0-9]*)+$/;

export const messageKeySchema = z
  .string()
  .regex(MESSAGE_KEY_PATTERN, 'a message key is dotted lowerCamelCase ASCII, e.g. "error.forbidden"');

export function isMessageKey(value: unknown): value is MessageKey {
  return typeof value === 'string' && MESSAGE_KEY_PATTERN.test(value);
}
